<template>
    <div class="flex flex-col border-2 rounded-lgr px-5 py-6 w-full">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 pb-5 border-b-2 w-full">
            <div class="title-body text-ink capitalize wrap-break-word min-w-0 sm:flex-1">{{ feedback.question }}</div>
            <div class="flex flex-wrap gap-1 justify-start sm:justify-end items-center">
                <template v-if="!feedback.report_cs_id">
                    <BaseTooltip side="bottom">
                        <template #trigger>
                            <BaseButton icon-only theme="neutral" :aria-label="t('feedback.request.copyUrl')" @click="handleCopyUrl">
                                <i
                                    :class="['text-base/none', urlCopied ? 'bi bi-check-lg text-notification-success-text' : 'bi bi-link-45deg text-button-tertiary-text']"
                                    aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>
                            {{ urlButtonText ?? t("feedback.request.copyUrl") }}
                        </template>
                    </BaseTooltip>
                    <BaseTooltip side="bottom">
                        <template #trigger>
                            <BaseButton icon-only theme="neutral" :aria-label="t('feedback.request.copyInvite')" @click="handleCopyLink">
                                <i
                                    :class="['text-base/none', linkCopied ? 'bi bi-check-lg text-notification-success-text' : 'bi bi-clipboard text-button-tertiary-text']"
                                    aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>
                            {{ buttonText ?? t("feedback.request.copyInvite") }}
                        </template>
                    </BaseTooltip>
                    <BaseTooltip side="bottom">
                        <template #trigger>
                            <BaseButton theme="neutral" :disabled="isReviewDisabled" class="min-w-40" @click="$emit('close-and-review', feedback)">
                                <div class="whitespace-nowrap">{{ t("feedback.request.card.closeAndReview") }}</div>
                            </BaseButton>
                        </template>
                        <template v-if="isReviewDisabled" #content>
                            {{ t("feedback.request.card.reviewDisabledTooltip", { count: minimumResponseCount }, minimumResponseCount) }}
                        </template>
                    </BaseTooltip>
                </template>
                <template v-else>
                    <BaseButton theme="secondary" class="min-w-40" @click="$emit('view-report', feedback)">
                        <div class="whitespace-nowrap">{{ t("feedback.request.card.viewReport") }}</div>
                    </BaseButton>
                    <BaseTooltip side="bottom">
                        <template #trigger>
                            <BaseButton theme="neutral" @click="$emit('reopen', feedback)">
                                <div class="whitespace-nowrap">{{ t("feedback.request.card.reopen") }}</div>
                            </BaseButton>
                        </template>
                        <template #content>{{ t("feedback.request.card.reopenTooltip") }}</template>
                    </BaseTooltip>
                </template>
                <BaseTooltip side="bottom">
                    <template #trigger>
                        <BaseButton icon-only theme="tertiary" :aria-label="t('common.delete')" @click="$emit('delete', feedback.id)">
                            <i class="bi bi-trash3 text-ink-muted leading-none" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template #content> {{ t("common.delete") }} </template>
                </BaseTooltip>
            </div>
        </div>
        <div class="flex flex-wrap gap-x-6 gap-y-1 pt-5">
            <div class="body text-ink-muted">{{ feedback.created_at }}</div>
            <div class="body text-ink-muted">{{ t("feedback.request.card.responseCount", { count: feedback.response_count }, feedback.response_count) }}</div>
        </div>
    </div>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    feedback: {
        type: Object,
        required: true,
    },
    buttonText: {
        type: String,
        default: null,
    },
    urlButtonText: {
        type: String,
        default: null,
    },
    minimumResponseCount: {
        type: Number,
        required: true,
    },
});

const { t } = useI18n();

const isReviewDisabled = computed(() => props.feedback.response_count < props.minimumResponseCount);

const emit = defineEmits(["delete", "copy-link", "copy-url", "close-and-review", "view-report", "reopen"]);

// Local tap-feedback state — gives users a visible confirmation on mobile where
// :hover and tooltips are unreliable. Per-card so neighboring cards don't flash.
const { isShowing: linkCopied, show: showLinkCopied } = useTimedShow({ duration: 1500 });
const { isShowing: urlCopied, show: showUrlCopied } = useTimedShow({ duration: 1500 });

const handleCopyLink = () => {
    emit("copy-link", props.feedback.id);
    showLinkCopied();
};

const handleCopyUrl = () => {
    emit("copy-url", props.feedback.id);
    showUrlCopied();
};
</script>
